import React, { useEffect, useState } from 'react';
import { Drawer, Table, Input, Checkbox, Select, Card, Button, Alert, message, Space } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { closeAiDrawer, resetAiExtraction } from '../../appRedux/actions/AiExtraction';
import { aiExtractionApi, httpClient } from '../../util/Api';
import { getAiActivePage } from '../../util/aiExtractionPageRef';

// property 字段 → 友好标签（与后端 canonical schema 对齐）
const PROPERTY_LABELS = {
  pptStreetNumber: 'Street Number',
  pptStreetName: 'Street Name',
  pptSuburb: 'Suburb',
  pptState: 'State',
  pptCityTown: 'City/Town',
  pptPostcode: 'Postcode',
  pptLot: 'Lot',
  pptLpps: 'Plan (LP/PS)',
  pptVolume: 'Volume',
  pptFolio: 'Folio',
  pptCrownAllotment: 'Crown Allotment',
  pptSection: 'Section',
  pptParish: 'Parish',
  pptCounty: 'County',
};

// JobContact 可编辑字段
const CONTACT_FIELDS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'company', label: 'Company' },
  { key: 'jobTitle', label: 'Job Title' },
  { key: 'email', label: 'Email' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'phone', label: 'Phone' },
  { key: 'regNum', label: 'Reg No.' },
  { key: 'companyRegNum', label: 'Company Reg No.' },
  { key: 'streetNumber', label: 'Street No.' },
  { key: 'streetName', label: 'Street Name' },
  { key: 'suburb', label: 'Suburb' },
  { key: 'state', label: 'State' },
  { key: 'postcode', label: 'Postcode' },
];

// camelCase 字段名 → Job 模型 PascalCase（取当前值对比列用）
const toPascal = (k) => k.charAt(0).toUpperCase() + k.slice(1);

/**
 * 文档 AI 抽取结果抽屉（全局挂 MainApp，Redux 驱动）。
 * 上：property 字段逐个勾选 + 可编辑 + 当前 Job 值对比；下：每条 JobContact 一张卡，含类型下拉 + 可编辑字段。
 * 确认后写回 Job 属性与新建 JobContact；关闭抽屉不清结果，可由浮动按钮再次打开。
 */
const AIExtractionDrawer = () => {
  const { drawerOpen, result, jobId, moduleId } = useSelector(({ aiExtraction }) => aiExtraction);
  const dispatch = useDispatch();

  const [propertyRows, setPropertyRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactTypeOptions, setContactTypeOptions] = useState([]);
  const [councilName, setCouncilName] = useState('');
  const [currentJob, setCurrentJob] = useState(null);
  const [saving, setSaving] = useState(false);

  // result 变化时初始化可编辑副本（Redux 中的结果不可变，这里拷一份本地编辑）
  useEffect(() => {
    if (!result) {
      setPropertyRows([]); setContacts([]); setContactTypeOptions([]); setCouncilName(''); setCurrentJob(null);
      return;
    }
    const property = result.property || {};
    const rows = Object.keys(PROPERTY_LABELS).map((key) => {
      const value = property[key] || '';
      return { key, label: PROPERTY_LABELS[key], value, include: value !== '' }; // 有值的默认勾选
    });
    setPropertyRows(rows);
    setCouncilName(property.councilName || '');

    const list = (result.contacts || []).map((c, i) => ({
      _gid: i,
      include: true,
      contactTypeId: c.contactTypeId || undefined,
      contactTypeName: c.contactTypeName || c.role || '',
      role: c.role || '',
      firstName: c.firstName || '', lastName: c.lastName || '', company: c.company || '', jobTitle: c.jobTitle || '',
      email: c.email || '', mobile: c.mobile || '', phone: c.phone || '', regNum: c.regNum || '', companyRegNum: c.companyRegNum || '',
      streetNumber: c.streetNumber || '', streetName: c.streetName || '', suburb: c.suburb || '', state: c.state || '', postcode: c.postcode || '',
    }));
    setContacts(list);
    setContactTypeOptions(result.contactTypeOptions || []);
  }, [result]);

  // 拉当前 Job 属性值，做对比列（best-effort，失败则留空）
  useEffect(() => {
    if (!drawerOpen || !jobId) return;
    httpClient.get('Business/Job/' + jobId)
      .then(({ data }) => { if (data && data.result) setCurrentJob(data.data); })
      .catch(() => { /* 对比列非必需，忽略 */ });
  }, [drawerOpen, jobId]);

  const setPropertyValue = (key, value) =>
    setPropertyRows((rows) => rows.map((r) => (r.key === key ? { ...r, value } : r)));
  const setPropertyInclude = (key, include) =>
    setPropertyRows((rows) => rows.map((r) => (r.key === key ? { ...r, include } : r)));
  const setContactField = (gid, field, value) =>
    setContacts((list) => list.map((c) => (c._gid === gid ? { ...c, [field]: value } : c)));

  const propertyColumns = [
    {
      title: 'Write', dataIndex: 'include', width: 60, align: 'center',
      render: (v, r) => <Checkbox checked={v} onChange={(e) => setPropertyInclude(r.key, e.target.checked)} />,
    },
    { title: 'Field', dataIndex: 'label', width: 150 },
    {
      title: 'AI Extracted (editable)', dataIndex: 'value',
      render: (v, r) => <Input size="small" value={v} onChange={(e) => setPropertyValue(r.key, e.target.value)} />,
    },
    {
      title: 'Current', dataIndex: 'key', width: 130,
      render: (k) => <span className="ai-extraction-current">{currentJob ? (currentJob[toPascal(k)] || '') : ''}</span>,
    },
  ];

  const handleConfirm = () => {
    // 组 property：仅勾选字段
    const property = {};
    propertyRows.forEach((r) => { if (r.include) property[r.key] = r.value; });
    const hasProperty = Object.keys(property).length > 0;

    // 组 contacts：仅勾选项
    const chosen = contacts.filter((c) => c.include);
    if (chosen.some((c) => !c.contactTypeId)) {
      message.warning('Please select a contact type for each included contact.');
      return;
    }
    const contactPayload = chosen.map((c) => ({
      contactTypeId: c.contactTypeId,
      firstName: c.firstName, lastName: c.lastName, company: c.company, jobTitle: c.jobTitle,
      email: c.email, mobile: c.mobile, phone: c.phone, regNum: c.regNum, companyRegNum: c.companyRegNum,
      streetNumber: c.streetNumber, streetName: c.streetName, suburb: c.suburb, state: c.state, postcode: c.postcode,
    }));

    if (!hasProperty && contactPayload.length === 0) {
      message.warning('Nothing selected to save.');
      return;
    }

    setSaving(true);
    aiExtractionApi.apply({ jobId, property: hasProperty ? property : null, contacts: contactPayload })
      .then(({ data }) => {
        if (data && data.result) {
          message.success(data.message || 'Saved.');
          refreshActivePageSections();
          dispatch(resetAiExtraction()); // 清结果并关闭（写回已落库）
        } else {
          message.error((data && data.message) || 'Save failed.');
        }
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || error?.message || 'Save failed.');
      })
      .finally(() => setSaving(false));
  };

  // 写回成功后刷新发起页的所有 section（property 与 JobContact 都会更新）
  const refreshActivePageSections = () => {
    const page = getAiActivePage();
    const children = page?.createPage?.current?.child;
    if (!children) return;
    children.forEach((ref) => {
      const sec = ref.current;
      if (sec && typeof sec.getSectionDataByDefault === 'function') {
        sec.getSectionDataByDefault();
      }
    });
  };

  return (
    <Drawer
      title="AI Document Analysis"
      placement="right"
      className="ai-extraction-drawer"
      visible={drawerOpen}
      onClose={() => dispatch(closeAiDrawer())}
      maskClosable={false}
      destroyOnClose={false}
    >
      {!result ? (
        <Alert type="info" message="No AI result yet." />
      ) : (
        <>
          <div className="ai-extraction-actionbar">
            <span className="ai-extraction-actionbar__hint">
              Review &amp; tick what to write. Contacts are created new; ticked property fields overwrite.
            </span>
            <Button type="primary" loading={saving} onClick={handleConfirm}>
              Confirm &amp; Write to HF
            </Button>
          </div>
          {/* Property —— 与主界面 section 同款 Card（标题大写、浅阴影） */}
          <Card className="ai-extraction-section Property" title="Property" headStyle={{ display: 'flex' }}>
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
              {councilName ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`Detected council/LGA: "${councilName}". Please set the LGA manually on the job if needed (not auto-mapped).`}
                />
              ) : null}
              <div className="data-table-div overflow-hidden">
                <Table
                  size="small"
                  bordered
                  rowKey="key"
                  pagination={false}
                  columns={propertyColumns}
                  dataSource={propertyRows}
                />
              </div>
            </Space>
          </Card>

          {/* Contacts —— 外层 section Card，内每条联系人一张 inner Card */}
          <Card className="ai-extraction-section Contact" title={`Contacts (${contacts.length})`} headStyle={{ display: 'flex' }}>
            {contacts.length === 0 ? (
              <Alert type="info" message="No contacts detected." />
            ) : (
              contacts.map((c) => (
                <Card
                  key={c._gid}
                  type="inner"
                  size="small"
                  className="ai-extraction-contact-card"
                  style={{ marginBottom: 16, opacity: c.include ? 1 : 0.55 }}
                  title={
                    <Checkbox
                      checked={c.include}
                      onChange={(e) => setContactField(c._gid, 'include', e.target.checked)}
                    >
                      {c.role ? `Detected role: ${c.role}` : 'Contact'}
                    </Checkbox>
                  }
                >
                  <div className="ai-extraction-contact-type">
                    <span className="ai-extraction-field-label">Contact Type *</span>
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Select contact type"
                      value={c.contactTypeId}
                      onChange={(v) => setContactField(c._gid, 'contactTypeId', v)}
                      options={contactTypeOptions}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <div className="ai-extraction-contact-grid">
                    {CONTACT_FIELDS.map((f) => (
                      <div key={f.key} className="ai-extraction-field">
                        <span className="ai-extraction-field-label">{f.label}</span>
                        <Input
                          size="small"
                          value={c[f.key]}
                          onChange={(e) => setContactField(c._gid, f.key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              ))
            )}
          </Card>
        </>
      )}
    </Drawer>
  );
};

export default AIExtractionDrawer;
